import { useState, useCallback } from 'react';
import { chequeService } from '../services/cheques/chequeService';

export const useCheques = (showSuccess = () => {}, showError = () => {}) => {
  const [loading, setLoading] = useState(false);

  const createCheque = useCallback(async (chequeData) => {
    setLoading(true);
    try {
      const response = await chequeService.createPrintedCheque(chequeData);

      if (response.success) {
        const chequeInfo = response.data?.Data || response.data;
        const beneficiaryName = chequeInfo?.BeneficiaryName || chequeData.beneficiaryName;
        const amount = chequeInfo?.ChqAmount || chequeData.chqAmount;

        showSuccess(
          `Cheque for ${beneficiaryName} (${parseFloat(amount).toLocaleString()}) added to print queue successfully`
        );
        return { success: true, data: response.data };
      } else {
        const errorMessage = response.message || 'Failed to create cheque';
        showError(errorMessage);
        return { success: false, message: errorMessage };
      }
    } catch (error) {
      const errorMessage = error.message || 'Error creating cheque';
      showError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [showSuccess, showError]);

  const createMultipleCheques = useCallback(async (chequesData) => {
    setLoading(true);
    try {
      const response = await chequeService.createMultipleCheques(chequesData);
      if (response.success) {
        showSuccess(`${chequesData.length} cheques created successfully`);
        return response.data;
      } else {
        showError(response.message || 'Failed to create cheques');
        return null;
      }
    } catch (error) {
      showError(error.message || 'Error creating cheques');
      return null;
    } finally {
      setLoading(false);
    }
  }, [showSuccess, showError]);

  const updateCheque = useCallback(async (id, chequeData) => {
    setLoading(true);
    try {
      const response = await chequeService.updatePrintedCheque(id, chequeData);
      if (response.success) {
        showSuccess('Cheque updated successfully');
        return response.data;
      } else {
        showError(response.message || 'Failed to update cheque');
        return null;
      }
    } catch (error) {
      showError(error.message || 'Error updating cheque');
      return null;
    } finally {
      setLoading(false);
    }
  }, [showSuccess, showError]);

  const deleteCheque = useCallback(async (id) => {
    setLoading(true);
    try {
      const response = await chequeService.deletePrintedCheque(id);
      if (response.success) {
        showSuccess('Cheque deleted successfully');
        return true;
      } else {
        showError(response.message || 'Failed to delete cheque');
        return false;
      }
    } catch (error) {
      showError(error.message || 'Error deleting cheque');
      return false;
    } finally {
      setLoading(false);
    }
  }, [showSuccess, showError]);

  const cancelCheque = useCallback(async (id, reason) => {
    setLoading(true);
    try {
      const response = await chequeService.voidCheque(id, reason);
      if (response.success) {
        showSuccess('Cheque cancelled successfully');
        return true;
      } else {
        showError(response.message || 'Failed to cancel cheque');
        return false;
      }
    } catch (error) {
      showError(error.message || 'Error cancelling cheque');
      return false;
    } finally {
      setLoading(false);
    }
  }, [showSuccess, showError]);

  const clearCheques = useCallback(async (chequeIds, clearedDate) => {
    setLoading(true);
    try {
      const response = await chequeService.markCleared(chequeIds, clearedDate);
      if (response.success) {
        showSuccess(`${chequeIds.length} cheque(s) marked as cleared`);
        return true;
      } else {
        showError(response.message || 'Failed to mark cheques as cleared');
        return false;
      }
    } catch (error) {
      showError(error.message || 'Error clearing cheques');
      return false;
    } finally {
      setLoading(false);
    }
  }, [showSuccess, showError]);

  const unclearCheques = useCallback(async (chequeIds) => {
    setLoading(true);
    try {
      const response = await chequeService.markUncleared(chequeIds);
      if (response.success) {
        showSuccess(`${chequeIds.length} cheque(s) marked as uncleared`);
        return true;
      } else {
        showError(response.message || 'Failed to mark cheques as uncleared');
        return false;
      }
    } catch (error) {
      showError(error.message || 'Error marking cheques as uncleared');
      return false;
    } finally {
      setLoading(false);
    }
  }, [showSuccess, showError]);

  const signCheques = useCallback(async (chequeIds) => {
    setLoading(true);
    try {
      const response = await chequeService.markSigned(chequeIds);
      if (response.success) {
        showSuccess(`${chequeIds.length} cheque(s) marked as signed`);
        return true;
      } else {
        showError(response.message || 'Failed to mark cheques as signed');
        return false;
      }
    } catch (error) {
      showError(error.message || 'Error marking cheques as signed');
      return false;
    } finally {
      setLoading(false);
    }
  }, [showSuccess, showError]);

  const unsignCheques = useCallback(async (chequeIds) => {
    setLoading(true);
    try {
      const response = await chequeService.markUnsigned(chequeIds);
      if (response.success) {
        showSuccess(`${chequeIds.length} cheque(s) reverted to printed`);
        return true;
      } else {
        showError(response.message || 'Failed to revert signed cheques');
        return false;
      }
    } catch (error) {
      showError(error.message || 'Error reverting signed cheques');
      return false;
    } finally {
      setLoading(false);
    }
  }, [showSuccess, showError]);

  const getCheques = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const response = await chequeService.getPrintedCheques(params);
      if (response.success) {
        return response;
      } else {
        showError(response.message || 'Failed to load cheques');
        return { success: false, data: [], message: response.message };
      }
    } catch (error) {
      showError(error.message || 'Error loading cheques');
      return { success: false, data: [], message: error.message };
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const getDashboardStatistics = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const response = await chequeService.getDashboardStatistics(params);
      if (response.success) {
        return response.data;
      } else {
        showError(response.message || 'Failed to load dashboard statistics');
        return null;
      }
    } catch (error) {
      showError(error.message || 'Error loading dashboard statistics');
      return null;
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const getUpcomingPayments = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const response = await chequeService.getUpcomingPayments(params);
      if (response.success) {
        return response;
      } else {
        showError(response.message || 'Failed to load upcoming payments');
        return { success: false, data: [], message: response.message };
      }
    } catch (error) {
      showError(error.message || 'Error loading upcoming payments');
      return { success: false, data: [], message: error.message };
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const getBankSummary = useCallback(async () => {
    try {
      const response = await chequeService.getBankSummary();
      if (response.success) {
        return response.data;
      }
      return [];
    } catch {
      return [];
    }
  }, []);

  const getChequeStatistics = useCallback(async () => {
    console.warn('⚠️ getChequeStatistics is deprecated. Use getDashboardStatistics instead.');
    return await getDashboardStatistics();
  }, [getDashboardStatistics]);

  const printSingleCheque = useCallback(async (chequeId, printOptions = {}) => {
    try {
      const response = await chequeService.getChequePdf(chequeId, printOptions);
      if (response.success) {
        showSuccess('Cheque PDF opened in new tab - Ready for viewing and printing');
        return response;
      } else {
        showError(response.message || 'Failed to prepare cheque PDF');
        return null;
      }
    } catch (error) {
      showError(error.message || 'Error preparing cheque PDF');
      return null;
    }
  }, [showSuccess, showError]);

  const printBulkCheques = useCallback(async (chequeIds, printOptions = {}) => {
    try {
      const response = await chequeService.printBulkCheques(chequeIds, printOptions);
      if (response.success) {
        showSuccess(`${chequeIds.length} cheques PDF opened in new tab - Ready for viewing and printing`);
        return response;
      } else {
        showError(response.message || 'Failed to prepare cheques PDF');
        return null;
      }
    } catch (error) {
      showError(error.message || 'Error preparing cheques PDF');
      return null;
    }
  }, [showSuccess, showError]);

  return {
    loading,
    createCheque,
    createMultipleCheques,
    updateCheque,
    deleteCheque,
    cancelCheque,
    clearCheques,
    unclearCheques,
    signCheques,
    unsignCheques,
    getCheques,
    getDashboardStatistics,
    getUpcomingPayments,
    getBankSummary,
    getChequeStatistics, // Deprecated - kept for backward compatibility
    printSingleCheque,
    printBulkCheques
  };
}; 